/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";

import docAPI from "api";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { showAttachmentIcon } from "selectors/attachmentSelectors";
import * as stores from "stores";
import { dialogApi, dialogTypes } from "components/dialogs";
import { download } from "utils/FileDownloadUtility";
import { SecurityUtils } from "dc-preview-utils/src/Utils/SecurityUtils";
import { lhpFeatures } from "constants/enums";
import { currLHPFeature } from "selectors/lhpSelectors";

class AttachmentsModel {
  @observable isReadyToLoadAttachments = false;
  @observable hasAttachments = false;
  @observable shouldShowAttachmentIcon = true;
  _getAttachmentsPromise = null;
  attachments = [];
  shouldShowExpandedComponent = false;

  @action
  setHasAttachments(value) {
    this.hasAttachments = value;
  }

  @action
  setIsReadyToLoadAttachments(value) {
    this.isReadyToLoadAttachments = value;
  }

  addAttachment(args) {
    this.attachments.push(args);
  }

  @action
  setShouldShowAttachmentIcon(value) {
    this.shouldShowAttachmentIcon = value;
  }

  loadAttachmentsList() {
    /*
      Load only if not loaded already. Currently we do not support
      modifications to a PDF file. So it is not possible for the
      attachments list to change once the file is opened. So, if we
      already have something in attachments collection, that is what we
      need to render.
    */
    if (this.attachments.length === 0) {
      this.getAttachmentsFromDocAPI()
        .then(() => {
          this.setIsReadyToLoadAttachments(true);
          this.postAttachmentsLoadHandler();
        })
        .catch(() => {
          this.postAttachmentsLoadHandler();
        });
    } else {
      this.setHasAttachments(true);
      this.setIsReadyToLoadAttachments(true);
    }
  }

  getAttachmentsFromDocAPI() {
    if (!this._getAttachmentsPromise) {
      this._getAttachmentsPromise = new Promise((resolve, reject) => {
        docAPI
          .loadAttachmentsList()
          .then(resp => {
            if (resp && resp.data) {
              let { attachments } = resp.data;
              // check if attachments exist
              if (attachments && Array.isArray(attachments) && attachments.length) {
                attachments.forEach(attachmentItem => {
                  this.addAttachment(attachmentItem);
                });
                this.setHasAttachments(true);
              }
            }
            resolve(this.attachments);
          })
          .catch(exception => {
            reject();
          });
      });
    }
    return this._getAttachmentsPromise;
  }

  postAttachmentsLoadHandler() {
    if (currLHPFeature(stores) === lhpFeatures.ATTACHMENTS && !this.hasAttachments) {
      stores.lhpStore.setCurrLHPFeature(lhpFeatures.NONE);
    }
  }

  cancelDialog() {
    dialogApi.hide();
  }

  downloadAttachment(attachmentName) {
    if (attachmentName) {
      return docAPI.fetchAttachmentFile(attachmentName).then(fetchAttachmentFileResponse => {
        if (fetchAttachmentFileResponse && fetchAttachmentFileResponse.data) {
          let { data } = fetchAttachmentFileResponse;
          let fileName = data.name;
          let fileBuffer = data.buffer;
          let fileSubtype = data.mimeType;

          if (fileName !== undefined && fileBuffer !== undefined && fileSubtype !== undefined) {
            if (SecurityUtils.isAttachmentTypeBlacklisted(fileName)) {
              dialogApi.show(dialogTypes.BLACKLISTED_FILE_DIALOG, {
                cancelDialog: this.cancelDialog
              });
            } else {
              download(fileBuffer, fileSubtype, fileName);
            }
          }
        }
      });
    }
    return Promise.reject("attachment name not found");
  }

  initializeAction() {
    const updateShouldShowAttachmentsDropin = shouldShowAttachments => {
      stores.lhpStore.attachmentsInitializeOpts.showCallback &&
        stores.lhpStore.attachmentsInitializeOpts.showCallback(shouldShowAttachments);
      this.shouldShowExpandedComponent = shouldShowAttachments;
    };
    subscribeToSelector(stores, showAttachmentIcon, updateShouldShowAttachmentsDropin);
  }
}

export { AttachmentsModel };
